import { createNamespacedDataProviderStore, extendStore } from './dataprovider';
import ApiClient from '../../client';
import Vue from 'vue';
import _ from 'lodash';
const store = createNamespacedDataProviderStore((setItems) => {
  return ApiClient.getBookSessions().then((response) => {
    setItems(response.data);
  });
});
export default extendStore(store, {
  getters: {
    getBookSessions: (state) => state.items,
    myAgendasWithSessionsGroupedByDay: (state, getters, rootState) => {
      const eventTimezone = rootState.CurrentEvent?.eventDetails?.timezone;
      const agendaTimezoneMode =
        rootState.CurrentEvent?.eventDetails?.agendaTimezoneMode;

      const dateFormatter =
        agendaTimezoneMode === 2 && eventTimezone
          ? new Intl.DateTimeFormat('en-CA', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              timeZone: eventTimezone
            })
          : null;

      return {
        sessions: _.groupBy(state.items, (session) => {
          if (dateFormatter) {
            return dateFormatter.format(new Date(session.startDate));
          }
          return Vue.moment(session.startDate).format('YYYY-MM-DD');
        })
      };
    },
    singleMyAgendaGroupedByDay: (state, getters) => {
      return getters.myAgendasWithSessionsGroupedByDay ?? { sessions: {} };
    },
    // Merged "everything in one place" view used by the My Agenda profile panel.
    // Combines bookmarked agenda sessions (kind=agenda), Confirmed AI meetings
    // (kind=meeting), and Open Sessions the participant is registered for
    // (kind=openSession). Each item is normalized to {id, kind, startDate, endDate,
    // title, subtitle, ...} and grouped by day, then sorted by start time within
    // each day. The original getters above are untouched so existing consumers
    // (Agenda.vue stage view, BookSessionsDataProvider, etc.) keep working.
    mergedMyAgendaGroupedByDay: (state, getters, rootState) => {
      const eventTimezone = rootState.CurrentEvent?.eventDetails?.timezone;
      const agendaTimezoneMode =
        rootState.CurrentEvent?.eventDetails?.agendaTimezoneMode;
      const dateFormatter =
        agendaTimezoneMode === 2 && eventTimezone
          ? new Intl.DateTimeFormat('en-CA', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              timeZone: eventTimezone
            })
          : null;

      const dayKey = (iso) => {
        if (!iso) return 'unscheduled';
        if (dateFormatter) return dateFormatter.format(new Date(iso));
        return Vue.moment(iso).format('YYYY-MM-DD');
      };

      const agendaItems = (state.items || []).map((s) => ({
        ...s,
        kind: 'agenda'
      }));

      const meetings = rootState.MyMeetings?.itemsByTab?.upcoming || [];
      // Only Confirmed meetings make it into My Agenda — Pending is shown on
      // /meetings until the sponsor accepts.
      const meetingItems = meetings
        .filter((m) => m.status === 'Confirmed')
        .map((m) => ({
          id: 'meeting:' + m.id,
          kind: 'meeting',
          sourceId: m.id,
          startDate: m.requestedStartAt,
          endDate: m.requestedEndAt,
          title: m.sponsorName
            ? `Meeting with ${m.sponsorName}`
            : 'Sponsor meeting',
          subtitle:
            m.location ||
            (m.sponsorBoothLabel ? `Booth ${m.sponsorBoothLabel}` : ''),
          sponsorId: m.sponsorId,
          sponsorName: m.sponsorName,
          location: m.location,
          status: m.status,
          // Fields BookSessions.vue's template touches — provide safe defaults so
          // the existing renderer doesn't crash on these new items.
          trackId: '0',
          speakers: [],
          docIds: [],
          description: m.message || '',
          liveUrl: '',
          myAgendaSize: 0,
          userIds: []
        }));

      const openSessions = rootState.OpenSessions?.items || [];
      const openSessionItems = openSessions
        .filter((s) => s.isRegistered && s.status !== 'Cancelled')
        .map((s) => ({
          id: 'openSession:' + s.id,
          kind: 'openSession',
          sourceId: s.id,
          startDate: s.startAt,
          endDate: s.endAt,
          title: s.title,
          subtitle: s.sponsorName || '',
          location: s.location,
          sponsorId: s.sponsorId,
          sponsorName: s.sponsorName,
          trackId: '0',
          speakers: [],
          docIds: [],
          description: s.description || '',
          liveUrl: '',
          myAgendaSize: 0,
          userIds: []
        }));

      const all = [...agendaItems, ...meetingItems, ...openSessionItems];
      const grouped = _.groupBy(all, (item) => dayKey(item.startDate));
      Object.keys(grouped).forEach((day) => {
        grouped[day].sort(
          (a, b) => new Date(a.startDate || 0) - new Date(b.startDate || 0)
        );
      });
      return { sessions: grouped };
    },
    getLoadingState: (state) => state.loading
  },
  mutations: {
    addOrDeleteBooksSesions(state, payload) {
      const index = state.items.findIndex((x) => x.id === payload.id);
      if (index > -1) {
        state.items.splice(index, 1);
      } else {
        state.items.push(payload);
      }
    },
    setSortedBooksSesions(state, payload) {
      state.items.sort((a, b) => {
        return new Date(a.startDate) - new Date(b.startDate);
      });
    },
    setLoadingSession(state, payload) {
      state.loading = payload;
    }
  },
  actions: {
    async addOrDeleteBooksSesions({ commit, getters }, payload) {
      await commit('setLoadingSession', true);

      const checkIfExist = getters.getBookSessions.some(
        (session) => payload.id === session.id
      );
      if (checkIfExist) {
        await ApiClient.deleteBookSession(payload).then((res) => {
          if (res.data) {
            commit('addOrDeleteBooksSesions', payload);
            commit('setLoadingSession', false);
          }
        });
      } else {
        await ApiClient.addBookSession(payload).then((res) => {
          if (res.data) {
            commit('addOrDeleteBooksSesions', payload);
            commit('setLoadingSession', false);
          }
        });
      }
    },
    loadSortedBooksSesions({ commit }) {
      commit('setSortedBooksSesions');
    }
  }
});
